import {
  FormJourneyItem,
  FormJourneyItemAddressAdditionalSettings,
  FormJourneyItemContactInfoAdditionalSettings,
} from '@plura/shared/types';

/**
 * Rough seconds a respondent spends answering each question type. Tuned for
 * plausibility, not precision — the welcome-screen badge only needs to set an
 * expectation, and it deliberately leans a touch fast: a quicker-sounding
 * form is more likely to get started.
 */
const SECONDS_PER_TYPE: Record<string, number> = {
  WELCOME_SCREEN: 0,
  END_SCREEN: 0,
  REDIRECT_SCREEN: 0,
  MULTI_QUESTION_PAGE: 0, // container — children are counted individually
  STATEMENT: 3,
  MULTIPLE_CHOICE: 5,
  DROPDOWN: 5,
  OPTION_SCALE: 4,
  NPS: 4,
  RATING: 4,
  SHORT_TEXT: 8,
  EMAIL: 7,
  WEBSITE: 7,
  NUMBER: 5,
  DATE: 7,
  PHONE_NUMBER: 8,
  LONG_TEXT: 20,
  OPEN_ENDED: 35,
  FILE: 25,
  SCHEDULER: 40,
};

const SECONDS_PER_SUB_FIELD = 6;

function visibleSubFieldCount(item: FormJourneyItem): number {
  const settings = item.additionalSettings as
    | (FormJourneyItemContactInfoAdditionalSettings & FormJourneyItemAddressAdditionalSettings)
    | null
    | undefined;
  if (!settings) return 0;
  return Object.values(settings).filter((f) => f && typeof f === 'object' && f.visible).length;
}

/** Estimated seconds to complete a form's questions. */
export function estimateCompletionSeconds(journeyItems: FormJourneyItem[] | null | undefined): number {
  if (!journeyItems?.length) return 0;
  return journeyItems.reduce((total, item) => {
    if (item.type === 'CONTACT_INFO' || item.type === 'ADDRESS') {
      const fields = visibleSubFieldCount(item);
      return total + (fields > 0 ? fields * SECONDS_PER_SUB_FIELD : 20);
    }
    return total + (SECONDS_PER_TYPE[item.type] ?? 8);
  }, 0);
}

/**
 * Short, friendly badge label: "Takes 30 seconds", "Takes 1 minute",
 * "Takes 4 minutes". Empty string when there's nothing to answer.
 * Rounding leans down — the faster the form sounds, the more people start it.
 */
export function formatCompletionEstimate(seconds: number): string {
  if (seconds <= 0) return '';
  if (seconds < 60) {
    // Nearest natural-sounding 15s step (15/30/45).
    const rounded = Math.max(15, Math.round(seconds / 15) * 15);
    if (rounded >= 60) return 'Takes 1 minute';
    return `Takes ${rounded} seconds`;
  }
  // Down-biased rounding: 1m38s reads "1 minute", 2m45s reads "3 minutes".
  const minutes = Math.max(1, Math.round(seconds / 60 - 0.25));
  return minutes <= 1 ? 'Takes 1 minute' : `Takes ${minutes} minutes`;
}
